/**
 * Utility functions for Attendance feature
 */

import type { Dochadzka } from "./types";

/**
 * Sort attendance records by date (descending) and then by time (descending)
 */
export function sortAttendanceByDateTime(dochadzka: Dochadzka[]): Dochadzka[] {
  return [...dochadzka].sort((a, b) => {
    const dateA = new Date(a.datum).getTime();
    const dateB = new Date(b.datum).getTime();

    if (dateA > dateB) return -1;
    if (dateA < dateB) return 1;

    if (!a.cas && !b.cas) return 0;
    if (!a.cas) return 1;
    if (!b.cas) return -1;

    const timeA = a.cas.split(":").map(Number);
    const timeB = b.cas.split(":").map(Number);

    if (timeA[0] > timeB[0]) return -1;
    if (timeA[0] < timeB[0]) return 1;
    if (timeA[1] > timeB[1]) return -1;
    if (timeA[1] < timeB[1]) return 1;

    return 0;
  });
}

/**
 * Calculate total duration from attendance records
 */
export function calculateTotalDuration(dochadzka: Dochadzka[]): number {
  return dochadzka.reduce((sum, d) => {
    if (!d.dlzkaTreningu) return sum;
    return sum + d.dlzkaTreningu;
  }, 0);
}

/**
 * Calculate total price from attendance records
 */
export function calculateTotalPrice(dochadzka: Dochadzka[]): number {
  return dochadzka.reduce((sum, d) => {
    if (!d.cena) return sum;
    const price = parseFloat(d.cena);
    return sum + (isNaN(price) ? 0 : price);
  }, 0);
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}

/**
 * Format time for display (removes seconds if present)
 */
export function formatTime(timeString: string | null | undefined): string {
  if (!timeString) return "";
  const parts = timeString.split(":");
  return `${parts[0]}:${parts[1]}`;
}

/**
 * Format duration for display
 */
export function formatDuration(duration: string | null | undefined): string {
  if (!duration) return "";
  const num = parseFloat(duration);
  return isNaN(num) ? "" : num.toFixed(1);
}

/**
 * Format price for display
 */
export function formatPrice(price: string | null | undefined): string {
  if (!price) return "";
  const num = parseFloat(price);
  return isNaN(num) ? "" : num.toFixed(2);
}

/**
 * Get client display name
 */
export function getClientDisplayName(klient: {
  alias: string;
  meno: string;
  priezvisko: string;
}): string {
  return `${klient.alias} - ${klient.meno} ${klient.priezvisko}`;
}
